/**
 * Many restaurant sites (e.g. MainDine) ship an empty HTML shell and render the menu
 * from a JS bundle. This module fetches those bundles and extracts menu-like data.
 */

const MAX_SCRIPT_BYTES = 6 * 1024 * 1024;

function normalizeWhitespace(text) {
  return String(text || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();
}

function parsePrice(raw) {
  if (raw == null || raw === "") return null;
  const m = String(raw).match(/\$\s*(\d{1,4}(?:[.,]\d{2})?)/);
  if (!m) return null;
  const num = Number.parseFloat(m[1].replace(",", "."));
  return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
}

/** True when the document looks like a client-rendered SPA shell. */
export function isSpaMenuShell(html) {
  const src = String(html || "");
  if (!src) return false;

  const hasModuleScript =
    /<script[^>]+type\s*=\s*["']module["']/i.test(src) ||
    /<script[^>]+src\s*=\s*["'][^"']*\/assets\/index-[^"']+\.js["']/i.test(src);

  const rootEmpty =
    /<div[^>]+id\s*=\s*["']root["'][^>]*>\s*<\/div>/i.test(src) ||
    /<div[^>]+id\s*=\s*["']root["'][^>]*>\s*$/im.test(src);

  const stripped = src
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const littleVisibleText = stripped.length < 400;

  return hasModuleScript && rootEmpty && littleVisibleText;
}

export function resolveScriptUrls(html, pageUrl) {
  const base = new URL(pageUrl);
  const urls = [];
  const re =
    /<script[^>]+(?:type\s*=\s*["']module["'][^>]+src\s*=\s*["']([^"']+)["']|src\s*=\s*["']([^"']+)["'][^>]+type\s*=\s*["']module["'])/gi;

  let match;
  while ((match = re.exec(html)) !== null) {
    const rel = match[1] || match[2];
    if (!rel || rel.startsWith("data:")) continue;
    try {
      urls.push(new URL(rel, base).href);
    } catch {
      // skip
    }
  }

  // Fallback: any same-origin app bundle under /assets/
  if (!urls.length) {
    const assetRe = /<script[^>]+src\s*=\s*["'](\/assets\/[^"']+\.js)["']/gi;
    while ((match = assetRe.exec(html)) !== null) {
      try {
        urls.push(new URL(match[1], base).href);
      } catch {
        // skip
      }
    }
  }

  return [...new Set(urls)];
}

function decodeJsStringLiteral(quote, body) {
  if (quote === "'") {
    return body.replace(/\\'/g, "'").replace(/\\n/g, "\n");
  }
  return body.replace(/\\"/g, '"').replace(/\\n/g, "\n");
}

/** Pull menu rows from minified React/Vite bundles (name/price packages, item/price tables). */
export function extractMenuItemsFromBundledJs(jsText) {
  const text = String(jsText || "");
  if (!text) return [];

  const items = [];
  const seen = new Set();

  const pushItem = (name, priceRaw, ingredients) => {
    const nameNorm = normalizeWhitespace(name);
    if (!nameNorm || nameNorm.length < 2) return;
    const key = `${nameNorm.toLowerCase()}|${priceRaw || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    const price = parsePrice(priceRaw);
    items.push({
      name: nameNorm,
      ingredients: ingredients ? normalizeWhitespace(ingredients) : null,
      price,
    });
  };

  // Catering/packages in SPAs often use name + price + contents (skip bare name/price tiers).
  const packageRe =
    /name:"((?:\\.|[^"\\])*)",price:"((?:\\.|[^"\\])*)",contents:"((?:\\.|[^"\\])*)"/g;
  let m;
  while ((m = packageRe.exec(text)) !== null) {
    const name = decodeJsStringLiteral('"', m[1]);
    const price = decodeJsStringLiteral('"', m[2]);
    const contents = decodeJsStringLiteral('"', m[3]);
    if (/\$/.test(price)) pushItem(name, price, contents);
  }

  const rowRe =
    /item:(['"])((?:\\.|[^\\])*?)\1,price:(['"])((?:\\.|[^\\])*?)\3/g;
  while ((m = rowRe.exec(text)) !== null) {
    const name = decodeJsStringLiteral(m[1], m[2]);
    const price = decodeJsStringLiteral(m[3], m[4]);
    if (/\$/.test(price)) pushItem(name, price, null);
  }

  // Alternate: price before item
  const rowRe2 =
    /price:(['"])((?:\\.|[^\\])*?)\1,item:(['"])((?:\\.|[^\\])*?)\3/g;
  while ((m = rowRe2.exec(text)) !== null) {
    const price = decodeJsStringLiteral(m[1], m[2]);
    const name = decodeJsStringLiteral(m[3], m[4]);
    if (/\$/.test(price)) pushItem(name, price, null);
  }

  return items;
}

export function buildSyntheticMenuHtml(items) {
  if (!items?.length) return "";
  const lines = items.map((it) => {
    const parts = [it.name];
    if (it.ingredients) parts.push(it.ingredients);
    if (it.price != null) parts.push(`$${it.price.toFixed(2)}`);
    else if (typeof it.price === "string") parts.push(it.price);
    return parts.join(" — ");
  });
  return `<!DOCTYPE html><html><body><pre id="menu-parser-spa-extract">${lines.join(
    "\n"
  )}</pre></body></html>`;
}

/**
 * @param {string} html
 * @param {string} pageUrl
 * @param {(url: string) => Promise<Response>} fetchFn
 */
export async function enrichSpaMenuHtml(html, pageUrl, fetchFn) {
  if (!isSpaMenuShell(html)) {
    return { html, items: [], enriched: false };
  }

  const scriptUrls = resolveScriptUrls(html, pageUrl);
  if (!scriptUrls.length) {
    return { html, items: [], enriched: false };
  }

  const allItems = [];

  for (const scriptUrl of scriptUrls.slice(0, 3)) {
    try {
      const res = await fetchFn(scriptUrl);
      if (!res.ok) continue;
      const len = Number(res.headers.get("content-length"));
      if (Number.isFinite(len) && len > MAX_SCRIPT_BYTES) continue;

      const buf = await res.arrayBuffer();
      if (buf.byteLength > MAX_SCRIPT_BYTES) continue;

      const jsText = new TextDecoder("utf-8").decode(buf);
      const chunkItems = extractMenuItemsFromBundledJs(jsText);
      for (const item of chunkItems) {
        const key = `${item.name}|${item.price}`;
        if (!allItems.some((x) => `${x.name}|${x.price}` === key)) {
          allItems.push(item);
        }
      }
    } catch {
      // try next script
    }
  }

  if (!allItems.length) {
    return { html, items: [], enriched: false };
  }

  return {
    html: buildSyntheticMenuHtml(allItems),
    items: allItems,
    enriched: true,
  };
}
